// Derived rollups over a ClaimProjection's months. These values are NOT
// stored on the projection — recomputed on demand from the month stream so
// the in-memory shape stays minimal and identities can't drift.
//
// All helpers are pure. UI code that needs a running cumulative typically
// wants the `*Series` variants (one value per month, same length as
// `months`); single-number aggregates use `total*` / `final*`.

export function getLtcPool(state: MonthlyState): number {
  return state.pools.baseBenefitPool + state.pools.extensionOfBenefitsPool;
}

function getEpLumpSumPaid(month: MonthlyEntry): number {
  const { strategyDeltas } = month.deltas;
  return typeof (strategyDeltas as { epLumpSumPaid?: number })?.epLumpSumPaid === 'number'
    ? (strategyDeltas as { epLumpSumPaid: number }).epLumpSumPaid
    : 0;
}

// The portion of the EP lump that covered care cost on its own row. Since
// P2-4b the lump's excess over the row's cost is banked into the indemnity
// surplus reserve (and counted as LTC only when later drained into
// `ltcPayoutToCost`), so cost-coverage totals must count the applied portion
// — not the full lump — to avoid double counting. Falls back to the full
// lump for strategies that don't emit the applied split.
function getEpLumpSumAppliedToCost(month: MonthlyEntry): number {
  const slot = month.deltas.strategyDeltas as { epLumpSumAppliedToCost?: number } | undefined;
  return typeof slot?.epLumpSumAppliedToCost === 'number'
    ? slot.epLumpSumAppliedToCost
    : getEpLumpSumPaid(month);
}

// Reads a numeric strategy-delta field (CM II surplus flow), defaulting to 0
// for strategies that don't emit it. Mirrors `getEpLumpSumPaid`.
function readStrategyDelta(month: MonthlyEntry, key: string): number {
  const slot = month.deltas.strategyDeltas as Record<string, unknown> | undefined;
  const value = slot?.[key];
  return typeof value === 'number' ? value : 0;
}

// Dollars paid by the policy in a single month (regular indemnity coverage
// plus any retroactive lump in strategyDeltas). Matches the predicate
// previously used to derive `firstPayoutRowIndex`.
export function getMonthPayout(month: MonthlyEntry): number {
  const { payout } = month.deltas;
  // `ltcCoveragePaid` is cost-covered dollars and can overlap with
  // `indemnityPayout`; monthly cash payout should count only one base payout
  // stream plus any EP lump. A mixed row (Asset Care's indemnity→COB
  // transition month, P2-9) pays AOB cash AND a COB reimbursement in the same
  // row — the reimbursement is reported separately in `cobReimbursementPaid`
  // (0 everywhere else) so the max() never drops either stream.
  const cobReimbursementPaid = readStrategyDelta(month, 'cobReimbursementPaid');
  const basePayout = Math.max(
    payout.indemnityPayout + cobReimbursementPaid,
    payout.ltcCoveragePaid,
  );
  return basePayout + getEpLumpSumPaid(month);
}

export function getFirstPayoutMonthIndex(months: MonthlyEntry[]): number | null {
  for (let i = 0; i < months.length; i += 1) {
    if (getMonthPayout(months[i]) > 0) return i;
  }
  return null;
}

// For joint projections, each spouse has their own "benefits begin" boundary
// (partner may start claiming long after the client has been paying). Returns
// the index in `months` of each claimant's first paying row. Rows that never
// pay don't get an entry — callers should treat "no entry" as "every row for
// this claimant is out-of-pocket." Single-life rows (claimant == null) are
// bucketed under the sentinel key `__single__` so a single map covers both
// shapes uniformly.
const SINGLE_LIFE_CLAIMANT_KEY = '__single__';

export function getFirstPayoutIndexByClaimant(
  months: MonthlyEntry[],
): Map<string, number> {
  const out = new Map<string, number>();
  for (let i = 0; i < months.length; i += 1) {
    const key = months[i].context.claimant ?? SINGLE_LIFE_CLAIMANT_KEY;
    if (out.has(key)) continue;
    if (getMonthPayout(months[i]) > 0) out.set(key, i);
  }
  return out;
}

// Lookup key for `getFirstPayoutIndexByClaimant` — exported so consumers
// can resolve a row without re-stringifying the sentinel themselves.
export function claimantKey(month: MonthlyEntry): string {
  return month.context.claimant ?? SINGLE_LIFE_CLAIMANT_KEY;
}

// Per-month cumulative LTC payout (running sum of indemnity + cost-coverage
// + retroactive lump). Final element is the lifetime total.
export function getCumulativeLtcPayoutSeries(months: MonthlyEntry[]): number[] {
  const out = new Array<number>(months.length);
  let running = 0;
  for (let i = 0; i < months.length; i += 1) {
    running += getMonthPayout(months[i]);
    out[i] = running;
  }
  return out;
}

export function getTotalLtcPayout(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) total += getMonthPayout(m);
  return total;
}

// Per-month cumulative out-of-pocket cost (running sum of payout.ineligibleCost).
export function getCumulativeIneligibleCostSeries(months: MonthlyEntry[]): number[] {
  const out = new Array<number>(months.length);
  let running = 0;
  for (let i = 0; i < months.length; i += 1) {
    running += months[i].deltas.payout.ineligibleCost;
    out[i] = running;
  }
  return out;
}

export function getTotalIneligibleCost(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) total += m.deltas.payout.ineligibleCost;
  return total;
}

// Per-month cumulative out-of-pocket room & board (Independent Living only; 0
// elsewhere). Room & board is never reimbursed, so it is a distinct
// out-of-pocket stream sitting alongside `ineligibleCost` (the care shortfall)
// rather than being folded into it — keeping the coverage identity
// `LTC coverage + ineligibleCost = care cost` intact.
export function getCumulativeRoomAndBoardCostSeries(months: MonthlyEntry[]): number[] {
  const out = new Array<number>(months.length);
  let running = 0;
  for (let i = 0; i < months.length; i += 1) {
    running += months[i].facts.roomAndBoardCost ?? 0;
    out[i] = running;
  }
  return out;
}

export function getTotalRoomAndBoardCost(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) total += m.facts.roomAndBoardCost ?? 0;
  return total;
}

// Room & board dollars met by indemnity cash across the projection (0 for
// reimbursement products, which never pay room & board). This is the portion of
// the gross room & board cost the policy actually covered, so out-of-pocket
// room & board = gross cost − this.
export function getTotalRoomAndBoardCovered(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) total += m.deltas.payout.roomAndBoardCovered ?? 0;
  return total;
}

// Room & board the client actually pays out of pocket: gross room & board cost
// minus any indemnity cash applied to it. Per-row max guards against a covered
// amount ever exceeding that row's cost (the identity check enforces this too).
export function getTotalRoomAndBoardOutOfPocket(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) {
    const cost = m.facts.roomAndBoardCost ?? 0;
    const covered = m.deltas.payout.roomAndBoardCovered ?? 0;
    total += Math.max(cost - covered, 0);
  }
  return total;
}

// Total out-of-pocket cost incurred across the projection: the care shortfall
// (`ineligibleCost`) plus the room & board the policy did NOT cover. Indemnity
// cash can meet part of Independent Living room & board (see
// `roomAndBoardCovered`); reimbursement never does, so for reimbursement this
// still equals ineligible cost + full room & board.
export function getTotalOutOfPocketCost(months: MonthlyEntry[]): number {
  return getTotalIneligibleCost(months) + getTotalRoomAndBoardOutOfPocket(months);
}

// ──────────────────────────────────────────────────────────────────────────
// LTC vs Non-LTC split rollups — match the funding-table taxonomy and the
// legacy engine's classification:
//   LTC payout      = dollars applied to care cost (ltcPayoutToCost + EP lump);
//                     includes banked surplus later drained to cover cost
//   Non-LTC payout  = indemnity surplus REMAINING at horizon (never used for
//                     care) + indemnity cash applied to room & board (non-care
//                     policy value delivered) + death benefit remaining at horizon
//
// Sum of getTotalLtcCoveragePayout + getTotalNonLtcPayout = getTotalPayout.
// ──────────────────────────────────────────────────────────────────────────

// Sum of dollars the policy applied to care cost: `ltcPayoutToCost` (direct
// cost coverage PLUS any banked indemnity surplus later drained to cover a
// shortfall) + any retroactive EP lump. Uses `ltcPayoutToCost`, not
// `ltcCoveragePaid`, to match the legacy `projectedCostCoverage` accumulator,
// which folds drained surplus into coverage (`applyIndemnityPayoutToPhase` →
// `costCoverageAddedToTotal`). For reimbursement-election products the two are
// equal (no surplus); the divergence applies to every indemnity-election
// product (CM II, OA Asset Care, MoneyGuard, hybrid life/IRA, generic hybrid,
// life-with-rider). The drained surplus is therefore
// classified as LTC here and excluded from `getTotalIndemnitySurplus` below —
// the two stay complementary so `getTotalPayout` is unchanged.
export function getTotalLtcCoveragePayout(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) {
    total += m.deltas.payout.ltcPayoutToCost + getEpLumpSumAppliedToCost(m);
  }
  return total;
}

export function getTotalIndemnityPayout(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) total += m.deltas.payout.indemnityPayout;
  return total;
}

// Indemnity surplus remaining at the end of the projection — the carried
// balance, NOT the gross accrual. Surplus that was later drained to cover care
// cost is counted under `getTotalLtcCoveragePayout` (as `ltcPayoutToCost`), so
// only the never-used remainder is the non-LTC cash component. This matches
// legacy, where `ltcPolicy.indemnitySurplus` is the accumulator's final value
// (`accumulator.indemnitySurplus += accrued − applied`, surfaced at
// `calcFundingSourceDerivatives.ts`). The balance telescopes to
// `Σ surplusAccrued − Σ surplusApplied` over the month stream (per-row
// identity `close = open + accrued − applied`); clamped at 0 defensively.
export function getTotalIndemnitySurplus(months: MonthlyEntry[]): number {
  let accrued = 0;
  let applied = 0;
  for (const m of months) {
    accrued += readStrategyDelta(m, 'indemnitySurplusAccrued');
    applied += readStrategyDelta(m, 'indemnitySurplusApplied');
  }
  return Math.max(accrued - applied, 0);
}

// Death benefit pool remaining at the end of the projection. Paid to
// beneficiaries on death; counts as a non-LTC return for ROI purposes.
export function getRemainingDeathBenefit(months: MonthlyEntry[]): number {
  if (months.length === 0) return 0;
  return months[months.length - 1].state.pools.deathBenefit;
}

export function getTotalNonLtcPayout(months: MonthlyEntry[]): number {
  // Indemnity cash applied to room & board is real policy value the client
  // received (and spent on non-care room & board), so it belongs in returns.
  // The diversion reduced `indemnitySurplusAccrued` by the same amount, so it
  // left `getTotalIndemnitySurplus` (remaining surplus) — re-home it here to
  // keep `getTotalPayout` whole (= Σ indemnityPayout + remaining DB).
  return (
    getTotalIndemnitySurplus(months) +
    getRemainingDeathBenefit(months) +
    getTotalRoomAndBoardCovered(months)
  );
}

// LTC coverage + non-LTC payout (indemnity surplus + remaining DB).
// This is the "total returns" figure that would feed an ROI calculation.
export function getTotalPayout(months: MonthlyEntry[]): number {
  return getTotalLtcCoveragePayout(months) + getTotalNonLtcPayout(months);
}

// Total inflation-adjusted care cost incurred across the projection,
// regardless of who paid. Equals policy LTC coverage + ineligible cost
// (modulo retroactive lump offsets).
export function getTotalProjectedCareCost(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) total += m.facts.effectiveCareCost;
  return total;
}
